/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSortBy, useTable } from "react-table";
import {
  GetAccount200ResponsePositionsInner,
  InstrumentTypeResponse,
} from "../../codegen-api";
import { MarketContext } from "../../contexts/MarketContext";
import { isInstrumentTypeEnabled } from "../../config/features";
import { MarketInstrumentContext } from "../../contexts/MarketInstrumentContext";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { useOrder } from "../../hooks/api/order/useOrder";
import { useRFQ } from "../../hooks/api/rfq/useRFQ";
import { IWSSMark } from "../../hooks/wss/model/markPrice";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { apiNum, calculatePnl } from "../../utils/math";
import { getPositionIdentity } from "../../utils/positionIdentity";
import { resolveRoi } from "../../utils/positionRoi";
import { knownPrice } from "../../utils/unknownNumbers";
import { Spinner } from "../shared/Spinner";
import { SpinnerContainerWrapper } from "../shared/Spinner/style";
import { DefaultCellForColumn } from "../shared/Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../shared/Table/DefaultHeaderForColumn";
import { IModifiedPosition } from "./model";
import useScreenSize from "../../hooks/screenSize/useScreenSize";
import PositionCardsView from "./PositionCardsView";
import PositionModals from "./PositionModals";
import PositionsTable from "./PositionsTable";
import { buildColumns } from "./columns";
import { buildSummaryRow } from "./summaryRow";
import { buildTablePositions } from "./helpers";
import { IPositionsProps } from "./types";

function Positions({
  positions,
  loading,
  selectedMarketType,
  instrumentMark,
  inLiquidation,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: IPositionsProps) {
  const { t } = useTranslation("app", { keyPrefix: "Positions" });
  const { t: tooltip } = useTranslation("tooltips");
  const { setMarket } = useContext(MarketContext);
  const { setOrderType, setExpiry, setStrike, getMarketPrecision } = useContext(
    MarketInstrumentContext
  );
  const { deleteRFQsByInstrumentId } = useRFQ(undefined, false);
  const { data: accountData } = useGetAccount();
  // TP/SL only exists where a trigger executor is deployed (Monad + Arc today).
  const { tpslSupported } = useOrder();
  // Card view below WIDTHS.M (≤1280): 1 column on phones (≤980) and a 2-column
  // grid on tablet (CSS handles the column count). The full table is
  // desktop-only (> 1280).
  const { isMediumScreen } = useScreenSize();

  // NOTE: IDs here are referring the position's instrument_id
  const [shareModalPositionId, setShareModalPositionId] = useState<string>();
  const [tpslModalPositionId, setTpslModalPositionId] = useState<string>();
  const [closingPositionId, setClosingPositionId] = useState<string>();
  const [viewDetailsForPositionId, setViewDetailsForPositionId] =
    useState<string>();
  const [adjustingMarginPositionId, setAdjustingMarginPositionId] =
    useState<string>();

  const [showClosePositionModal, setShowClosePositionModal] =
    useState<boolean>(false);

  const modifiedPositions: IModifiedPosition[] = useMemo(
    () =>
      positions.map((p) => {
        const mark: IWSSMark | undefined = instrumentMark[p.instrument_name];

        // Live mark first, then the row's own. Either may be unknown: the
        // backend sends `null` when it cannot resolve the market, and
        // `Number(null)` priced the position at $0 (a fabricated total loss).
        const markPrice = knownPrice(mark?.mark_price) ?? knownPrice(p.mark_price);
        const positionValue =
          markPrice !== undefined ? markPrice * Number(p.amount) : undefined;
        const entryValue = Number(p.avg_entry_price || 0) * Number(p.amount);
        // Prefer the backend's authoritative PnL; fall back to a mark-based
        // client calc only when the API omits it AND a mark is known. Matches
        // PositionDetailsModal so the table, the details modal, and the summary
        // total never disagree.
        const unrealized_pnl =
          apiNum(p.unrealized_pnl) ??
          (positionValue !== undefined
            ? calculatePnl(entryValue, positionValue, p.side)
            : undefined);
        // Single source of truth for displayed ROE (see utils/positionRoi.ts):
        // prefers the backend's return_on_equity, else the documented
        // margin-basis chain. The details modal and PnL card call the same
        // function, so the table can never show a different ROE than they do.
        // No PnL, no ROE.
        const roi =
          unrealized_pnl !== undefined
            ? resolveRoi(unrealized_pnl, p)
            : undefined;

        const { amount_precision, price_precision } = getMarketPrecision(
          p.asset,
          p.instrument_type
        );
        return {
          ...p,
          mark_price: markPrice !== undefined ? String(markPrice) : undefined,
          unrealized_pnl:
            unrealized_pnl !== undefined ? String(unrealized_pnl) : undefined,
          roi,
          positionValue,
          amountPrecision: amount_precision,
          pricePrecision: price_precision,
        };
      }),
    [getMarketPrecision, instrumentMark, positions]
  );

  const positionById = useCallback(
    (positionId?: string) => {
      if (!positionId) {
        return undefined;
      }

      // Match on the full identity only. The old `|| instrument_id === id`
      // fallback matched ANY leg on that instrument regardless of side or
      // margin type, so the Close / Adjust Margin / TP-SL modals could act on a
      // position the trader never selected. `getPositionIdentity` is total for
      // any position carrying an instrument, so the fallback is unnecessary.
      return modifiedPositions.find(
        (position) => getPositionIdentity(position) === positionId
      );
    },
    [modifiedPositions]
  );

  const onClosePositionWithId = useCallback((instrument_id: string) => {
    setClosingPositionId(instrument_id);
    setShowClosePositionModal(true);
  }, []);

  const onClosePosition = useCallback(
    (pos: GetAccount200ResponsePositionsInner) => {
      onClosePositionWithId(getPositionIdentity(pos) || pos.instrument_id);
    },
    [onClosePositionWithId]
  );

  const onViewPositionDetails = useCallback(
    (pos: GetAccount200ResponsePositionsInner) => {
      setViewDetailsForPositionId(getPositionIdentity(pos) || pos.instrument_id);
    },
    []
  );

  const onSharePosition = useCallback(
    (pos: GetAccount200ResponsePositionsInner) => {
      setViewDetailsForPositionId(getPositionIdentity(pos) || pos.instrument_id);
    },
    []
  );

  // Now create all table positions
  const tablePositions: IModifiedPosition[] =
    buildTablePositions(modifiedPositions);

  const columns: ITableColumn<IModifiedPosition>[] = useMemo(
    () =>
      buildColumns({
        t,
        tooltip,
        accountData,
        modifiedPositions,
        selectedMarketType,
        inLiquidation,
        onClosePositionWithId,
        setViewDetailsForPositionId,
        setShareModalPositionId,
        setAdjustingMarginPositionId,
        onEditTpsl: tpslSupported ? setTpslModalPositionId : undefined,
      }),
    [
      accountData?.leverages,
      inLiquidation,
      modifiedPositions,
      onClosePositionWithId,
      selectedMarketType,
      t,
      tooltip,
      tpslSupported,
    ]
  );

  const summaryRow = useMemo(
    () => buildSummaryRow({ columns, modifiedPositions, t }),
    [columns, modifiedPositions, t]
  );

  const onClosePositionModalHide = useCallback(() => {
    const closingPosition = positionById(closingPositionId);
    if (closingPosition?.instrument_id) {
      deleteRFQsByInstrumentId(closingPosition.instrument_id);
    }

    setShowClosePositionModal(false);
  }, [closingPositionId, deleteRFQsByInstrumentId, positionById]);

  const onAdjustMarginModalHide = useCallback(() => {
    setAdjustingMarginPositionId(undefined);
  }, []);

  const onPositionDetailsModalHide = useCallback(() => {
    setViewDetailsForPositionId(undefined);
  }, []);

  const onSharePositionModalHide = useCallback(() => {
    setShareModalPositionId(undefined);
  }, []);

  const onTPSLForPositionModalHide = useCallback(() => {
    setTpslModalPositionId(undefined);
  }, []);

  const onSelectPosition = useCallback(
    (position: GetAccount200ResponsePositionsInner) => {
      const derivative = position.option
        ? InstrumentTypeResponse.Option
        : InstrumentTypeResponse.Perpetual;
      // See Orders/index.tsx: a position on a switched-off vertical stays
      // visible but is not a navigation target.
      if (!isInstrumentTypeEnabled(derivative)) {
        return;
      }
      setMarket({ derivative, asset: position.asset });
      if (position.option) {
        setOrderType(position.option?.option_type);
        setExpiry(Number(position.option?.expiry));
        setStrike(position.option?.strike);
      }
    },
    [setExpiry, setMarket, setOrderType, setStrike]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<IModifiedPosition>(
      {
        columns,
        data: tablePositions || [],
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        },
        autoResetSortBy: false,
      } as any,
      useSortBy
    );

  if (loading) {
    return (
      <SpinnerContainerWrapper>
        <Spinner />
      </SpinnerContainerWrapper>
    );
  }

  return (
    <>
      <PositionModals
        positionById={positionById}
        adjustingMarginPositionId={adjustingMarginPositionId}
        onAdjustMarginModalHide={onAdjustMarginModalHide}
        showClosePositionModal={showClosePositionModal}
        closingPositionId={closingPositionId}
        onClosePositionModalHide={onClosePositionModalHide}
        viewDetailsForPositionId={viewDetailsForPositionId}
        onPositionDetailsModalHide={onPositionDetailsModalHide}
        shareModalPositionId={shareModalPositionId}
        onSharePositionModalHide={onSharePositionModalHide}
        tpslModalPositionId={tpslModalPositionId}
        onTPSLForPositionModalHide={onTPSLForPositionModalHide}
      />
      {isMediumScreen && (
        <PositionCardsView
          modifiedPositions={modifiedPositions}
          accountData={accountData}
          t={t}
          onSelectPosition={onSelectPosition}
          onClosePositionWithId={onClosePositionWithId}
          onEditTpsl={tpslSupported ? setTpslModalPositionId : undefined}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
        />
      )}
      {!isMediumScreen && (
        <PositionsTable
          getTableProps={getTableProps}
          getTableBodyProps={getTableBodyProps}
          headerGroups={headerGroups}
          rows={rows}
          prepareRow={prepareRow}
          summaryRow={summaryRow}
          columns={columns}
          inLiquidation={inLiquidation}
          onSelectPosition={onSelectPosition}
          onClosePosition={onClosePosition}
          onViewPositionDetails={onViewPositionDetails}
          onSharePosition={onSharePosition}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
        />
      )}
    </>
  );
}

export default Positions;
